/**
 * shared/excel/sheet.js — mô tả một bảng tính dưới dạng dữ liệu thuần.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * VÌ SAO TÁCH RIÊNG KHỎI VIỆC GHI FILE
 *
 * Phần khó của việc xuất Excel không phải nén file, mà là dựng đúng công
 * thức: ô nào tham chiếu ô nào, hàng nào cộng hàng nào. Tách phần đó ra thành
 * hàm thuần thì kiểm thử được bằng `node --test` mà không cần mở Excel.
 *
 * Phần ghi file nằm ở src/lib/xuatExcel.js.
 * ─────────────────────────────────────────────────────────────────────────
 *
 * MỘT NGUYÊN TẮC CHI PHỐI TOÀN BỘ: file xuất ra phải chứa CÔNG THỨC SỐNG,
 * không phải kết quả đã tính sẵn.
 *
 * Người nhận — nhà đầu tư, kế toán, đồng sáng lập — sẽ bấm vào ô để xem nó
 * được tính thế nào. Một bảng toàn số cứng là bảng không kiểm chứng được, và
 * người đọc có kinh nghiệm sẽ ngờ ngay. Bảng có công thức thì họ tự sửa giả
 * định và xem kết quả đổi — đó mới là thứ họ muốn.
 */

/** Chuyển chỉ số cột 0-based thành chữ cái: 0 → A, 25 → Z, 26 → AA. */
export function cotChu(i) {
  let s = '';
  let n = i;
  do {
    s = String.fromCharCode(65 + (n % 26)) + s;
    n = Math.floor(n / 26) - 1;
  } while (n >= 0);
  return s;
}

/** Địa chỉ ô kiểu A1 từ chỉ số hàng/cột 0-based. */
export function o(cot, hang) {
  return `${cotChu(cot)}${hang + 1}`;
}

/** Dải ô kiểu A1:A12. */
export function dai(cot1, hang1, cot2, hang2) {
  return `${o(cot1, hang1)}:${o(cot2, hang2)}`;
}

/* ------------------------------------------------------------------ */
/* Các loại ô                                                           */
/* ------------------------------------------------------------------ */

/**
 * BẢNG MÀU VÀ KIỂU Ô
 *
 * Theo quy ước của giới tài chính — cũng là quy ước trong file mẫu mà người
 * đọc bảng (nhà đầu tư, kế toán, chuyên viên phân tích) đã quen:
 *   · chữ XANH DƯƠNG trên nền VÀNG  = ô người dùng nhập tay
 *   · chữ ĐEN                        = ô công thức, không sửa
 *   · dải màu xanh nhạt              = tiêu đề nhóm
 *   · dải xanh đậm chữ trắng         = tên tab
 * Nhờ quy ước này, người nhận nhìn một cái là biết được phép sửa ô nào.
 */
export const MAU = Object.freeze({
  NEN_TIEU_DE: 'FF0F3057',   // xanh mực — dải tên tab
  NEN_NHOM: 'FFD6E6F2',      // xanh nhạt — dải tiêu đề nhóm
  NEN_NHAP: 'FFFFF9D6',      // vàng nhạt — ô nhập
  CHU_NHAP: 'FF0000FF',      // xanh dương — số nhập tay
  CHU_TRANG: 'FFFFFFFF',
  CHU_DEN: 'FF14181C',
  CHU_NHAT: 'FF6B7280',
  VIEN: 'FFBFCBD6',
  VIEN_DAM: 'FF0F3057',
});

const FONT = (tuyChon = {}) => ({ ten: 'Arial', co: 10, dam: false, mau: MAU.CHU_DEN, ...tuyChon });

/**
 * Kiểu ô. Mỗi kiểu mô tả font, nền, viền, căn lề — src/lib/xuatExcel.js dịch
 * sang styles.xml. Thêm kiểu mới ở đây, không sửa phần ghi file.
 */
export const KIEU = Object.freeze({
  'mac-dinh': { font: FONT() },
  'tieu-de-tab': { font: FONT({ co: 14, dam: true, mau: MAU.CHU_TRANG }), nen: MAU.NEN_TIEU_DE, canh: 'left' },
  'phu-de-tab': { font: FONT({ mau: MAU.CHU_TRANG }), nen: MAU.NEN_TIEU_DE, canh: 'left' },
  nhom: { font: FONT({ dam: true }), nen: MAU.NEN_NHOM, canh: 'left' },
  nhan: { font: FONT(), canh: 'left' },
  'nhan-dam': { font: FONT({ dam: true }), canh: 'left' },
  'nhan-con': { font: FONT(), canh: 'left', thut: 1 },
  'cot-tieu-de': { font: FONT({ dam: true }), vien: 'duoi', canh: 'right' },
  'cot-tieu-de-trai': { font: FONT({ dam: true }), vien: 'duoi', canh: 'left' },
  'don-vi': { font: FONT({ mau: MAU.CHU_NHAT }), canh: 'center' },
  nhap: { font: FONT({ mau: MAU.CHU_NHAP }), nen: MAU.NEN_NHAP, vien: 'hop', canh: 'right' },
  tinh: { font: FONT(), vien: 'hop', canh: 'right' },
  tong: { font: FONT({ dam: true }), vien: 'tren', canh: 'right' },
  'ghi-chu': { font: FONT({ co: 9, mau: MAU.CHU_NHAT, nghieng: true }), canh: 'left' },
});

/* ------------------------------------------------------------------ */
/* Các loại ô                                                           */
/* ------------------------------------------------------------------ */

/** Ô chữ thường. */
export const chu = (v, kieu = 'nhan') => ({ loai: 'chu', v: String(v ?? ''), kieu });

/** Nhãn in đậm — đầu mục, tên dòng tổng. */
export const tieuDe = (v) => ({ loai: 'chu', v: String(v ?? ''), dam: true, kieu: 'nhan-dam' });

/** Dải tên tab, đặt ở dòng 1 của mỗi bảng. */
export const tieuDeTab = (v) => ({ loai: 'chu', v: String(v ?? ''), kieu: 'tieu-de-tab' });
export const phuDeTab = (v) => ({ loai: 'chu', v: String(v ?? ''), kieu: 'phu-de-tab' });

/** Dải tiêu đề nhóm trong bảng. */
export const nhomMuc = (v) => ({ loai: 'chu', v: String(v ?? ''), kieu: 'nhom' });

/** Ô tiêu đề cột — in đậm, gạch chân. */
export const cotTieuDe = (v, canhTrai = false) => ({ loai: 'chu', v: String(v ?? ''), kieu: canhTrai ? 'cot-tieu-de-trai' : 'cot-tieu-de' });

/** Nhãn con, thụt vào một cấp. */
export const nhanCon = (v) => ({ loai: 'chu', v: String(v ?? ''), kieu: 'nhan-con' });

/** Ký hiệu đơn vị (#, %, đ) đặt cạnh nhãn. */
export const donVi = (v) => ({ loai: 'chu', v: String(v ?? ''), kieu: 'don-vi' });

/**
 * Ô số NGƯỜI DÙNG NHẬP. Hiện chữ xanh trên nền vàng — người nhận biết ngay
 * đây là ô được phép sửa.
 */
export const so = (v, dinhDang = 'SO') => ({
  loai: 'so',
  v: Number.isFinite(Number(v)) ? Number(v) : 0,
  dinhDang,
  kieu: 'nhap',
});

/**
 * Ô nhập CHƯA ĐIỀN: vẫn tô nền vàng để người nhận biết chỗ cần nhập, nhưng
 * không ghi số 0. Ghi 0 sẽ biến "chưa điền" thành "bằng 0" và làm sai công thức.
 */
export const soTrong = (dinhDang = 'SO') => ({ loai: 'soTrong', dinhDang, kieu: 'nhap' });

/** Ô nhập: có số thì ghi số, ô trống thì để trống (không thành 0). */
export const soHoacTrong = (v, dinhDang = 'SO') =>
  (v === '' || v == null || !Number.isFinite(Number(v)) ? soTrong(dinhDang) : so(v, dinhDang));

/** Ô số chỉ để hiển thị, không phải chỗ nhập (ví dụ số đã chốt ở bảng tham chiếu). */
export const soTinh = (v, dinhDang = 'SO') => ({ ...so(v, dinhDang), kieu: 'tinh' });

/**
 * Ô công thức. Đây là loại ô quan trọng nhất của cả module.
 * `f` viết như trong Excel, không có dấu bằng đầu dòng: 'B5-C5'
 */
export const congThuc = (f, dinhDang = 'SO', kieu = 'tinh') => ({ loai: 'congThuc', f, dinhDang, kieu });

/** Dòng tổng — in đậm, kẻ vạch phía trên. */
export const congThucTong = (f, dinhDang = 'SO') => congThuc(f, dinhDang, 'tong');

/** Ô trống. */
export const trong = () => ({ loai: 'trong' });

/** Ô ghi chú — chữ nhỏ, màu nhạt, dùng để giải thích giả định. */
export const ghiChu = (v) => ({ loai: 'chu', v: String(v ?? ''), nhat: true, kieu: 'ghi-chu' });

/* ------------------------------------------------------------------ */
/* Định dạng số                                                         */
/* ------------------------------------------------------------------ */

/**
 * Mã định dạng theo chuẩn Excel, dùng quy ước của báo cáo tài chính:
 * số âm trong ngoặc, số 0 hiện dấu gạch ngang cho bảng đỡ rối.
 */
export const DINH_DANG = Object.freeze({
  SO: '#,##0;(#,##0);-',
  TIEN: '#,##0;(#,##0);-',
  TIEN_TRIEU: '#,##0.0,, "triệu";(#,##0.0,, "triệu");-',
  PHAN_TRAM: '0.0%;(0.0%);-',
  PHAN_TRAM_NGUYEN: '0%;(0%);-',
  THANG: '0.0;(0.0);-',
  CHU: '@',
  NGAY: 'dd/mm/yyyy',
});

/* ------------------------------------------------------------------ */
/* Bảng tính                                                            */
/* ------------------------------------------------------------------ */

/**
 * @param {string} ten     tên tab, Excel giới hạn 31 ký tự và cấm : \ / ? * [ ]
 * @param {Array}  hang    mảng các hàng, mỗi hàng là mảng ô
 * @param {Array}  [rongCot] độ rộng từng cột
 */
export function bang(ten, hang, rongCot = [], tuyChon = {}) {
  const sach = String(ten).replace(/[:\\/?*[\]]/g, ' ').slice(0, 31);
  return {
    ten: sach,
    hang,
    rongCot,
    // dongBang: ô đầu tiên còn cuộn được, ví dụ 'B5' giữ cố định cột A và 4 dòng đầu.
    dongBang: tuyChon.dongBang || null,
    // Bảng tài chính đọc dễ hơn khi tắt lưới ô; viền do kiểu ô quyết định.
    anLuoi: tuyChon.anLuoi !== false,
  };
}

/** Một tệp gồm nhiều bảng. */
export function tep(cacBang, thongTin = {}) {
  if (!Array.isArray(cacBang) || cacBang.length === 0) {
    throw new Error('Tệp phải có ít nhất một bảng');
  }
  const ten = new Set();
  for (const b of cacBang) {
    if (ten.has(b.ten)) throw new Error(`Trùng tên bảng: ${b.ten}`);
    ten.add(b.ten);
  }
  return { cacBang, thongTin };
}

/* ------------------------------------------------------------------ */
/* Khối dùng lại                                                        */
/* ------------------------------------------------------------------ */

/** Dải tên tab trải hết chiều rộng bảng. */
export function dongTenTab(ten, phu = '', soCot = 6) {
  const hang = [tieuDeTab(ten)];
  for (let i = 1; i < Math.max(1, soCot); i++) hang.push(phuDeTab(i === 1 ? phu : ''));
  return hang;
}

/** Dải tiêu đề nhóm trải hết chiều rộng bảng. */
export function dongNhom(nhan, soCot = 6) {
  const hang = [nhomMuc(nhan)];
  for (let i = 1; i < Math.max(1, soCot); i++) hang.push(nhomMuc(''));
  return hang;
}

/**
 * Khối tiêu đề đặt trên cùng mỗi tệp xuất ra.
 *
 * Vì sao có: file này được gửi đi — cho nhà đầu tư, kế toán, đồng sáng lập. Mở
 * ra mà không biết của ai, tính ngày nào, thì người nhận phải hỏi lại.
 *
 * Chèn được ở đầu mà không phá công thức vì mọi địa chỉ trong các workbook đều
 * tính từ `hang.length` tại thời điểm dựng, không ghi cứng số dòng. Nếu sau này
 * có workbook ghi cứng số dòng thì KHÔNG dùng khối này cho nó.
 */
export function khoiTieuDe(congTy, ngayXuat, soCot = 6, tenBang = '') {
  return [
    dongTenTab(String(congTy || 'fundraising.vn').slice(0, 120), tenBang, soCot),
    [ghiChu(
      `fundraising.vn, xuất ngày ${ngayXuat || new Date().toISOString().slice(0, 10)}`
      + '. Kết quả tham khảo, không phải tư vấn đầu tư.',
    )],
    [trong()],
  ];
}

/**
 * Khối "giả định" đặt ở đầu mỗi bảng.
 *
 * Vì sao bắt buộc: mọi công thức phía dưới trỏ về khối này thay vì nhúng số
 * cứng. Người nhận sửa một ô giả định là cả bảng tính lại — đó là khác biệt
 * giữa một bảng để đọc và một bảng để dùng.
 */
export function khoiGiaDinh(muc, hangBatDau = 0) {
  const hang = [[tieuDe('GIẢ ĐỊNH'), trong()]];
  const diaChi = {};
  muc.forEach((m, i) => {
    hang.push([chu(m.nhan), so(m.giaTri, m.dinhDang || 'SO'), ghiChu(m.ghiChu || '')]);
    diaChi[m.key] = o(1, hangBatDau + 1 + i);
  });
  hang.push([trong()]);
  return { hang, diaChi, soHang: hang.length };
}
